import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessagingService } from '../../../../services/messagingServices/messaging.service';
import { IAlertService } from '../../../../iservices/iAlert';
import { IMessagingProviderCredentials } from '../../../../iModels/iCommonModels/messaging.model';

export interface IAddPinBotTemplateData {
  id?: string;
  name?: string;
  category?: string;
  language?: string;
  body?: string;
  isEdit?: boolean;
  credentials: IMessagingProviderCredentials;
}

@Component({
  selector: 'app-add-pinbot-template',
  templateUrl: './add-pinbot-template.component.html',
  styleUrls: ['./add-pinbot-template.component.scss']
})
export class AddPinBotTemplateComponent {
  templateForm: FormGroup;
  isEdit = false;
  categoryOptions = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
  readonly contactFieldOptions = [
    { value: 'Name', label: 'Customer name' },
    { value: 'Email', label: 'Customer email' },
    { value: 'Phone', label: 'Customer phone' }
  ];

  constructor(
    public dialogRef: MatDialogRef<AddPinBotTemplateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAddPinBotTemplateData,
    private formBuilder: FormBuilder,
    private messagingService: MessagingService,
    @Inject('IAlertService') private alertService: IAlertService
  ) {
    this.isEdit = !!(data && data.isEdit);
    this.templateForm = this.formBuilder.group({
      name: [{ value: (data && data.name) || '', disabled: this.isEdit }, Validators.required],
      category: [(data && data.category) || 'MARKETING', Validators.required],
      language: [(data && data.language) || 'en_US', Validators.required],
      body: [(data && data.body) || '', Validators.required],
      placeholderBindings: this.formBuilder.array([]),
      extraJson: ['{}', this.validExtraJson]
    });
    this.updatePlaceholderBindings(this.templateForm.get('body').value);
    this.templateForm.get('body').valueChanges.subscribe((body) => this.updatePlaceholderBindings(body));
  }

  submit(): void {
    if (this.templateForm.invalid) { return; }
    const value = this.templateForm.getRawValue();
    const creds = this.data.credentials || {};
    const payload = {
      name: value.name,
      category: value.category,
      language: value.language,
      components: [{ type: 'BODY', text: value.body }],
      extraJson: this.prepareExtraJson(value.extraJson)
    };
    const request$ = this.isEdit
      ? this.messagingService.updatePinBotTemplate({ ApiKey: creds.ApiKey, TemplateId: this.data.id, Payload: payload })
      : this.messagingService.createPinBotTemplate({ ApiKey: creds.ApiKey, WabaId: creds.WabaId, Payload: payload });
    request$.subscribe(() => {
      this.alertService.successMsg(this.isEdit ? 'Template updated successfully' : 'Template created successfully', false);
      this.dialogRef.close(payload);
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  get placeholderBindings(): FormArray {
    return this.templateForm.get('placeholderBindings') as FormArray;
  }

  get preparedExtraJson(): string {
    const extraJson = this.templateForm.get('extraJson');
    return extraJson && extraJson.valid ? this.prepareExtraJson(extraJson.value) : '';
  }

  private updatePlaceholderBindings(body: string): void {
    const selectedFields = new Map(
      this.placeholderBindings.controls.map((binding) => [
        binding.get('token').value,
        binding.get('field').value
      ])
    );
    const tokens = Array.from(new Set(
      (body || '').match(/{{\s*(\d+)\s*}}/g) || []
    )).sort((left, right) => this.placeholderIndex(left) - this.placeholderIndex(right));

    this.placeholderBindings.clear();
    tokens.forEach((token) => {
      this.placeholderBindings.push(this.formBuilder.group({
        token: [token],
        field: [selectedFields.get(token) || '', Validators.required]
      }));
    });
  }

  private placeholderIndex(token: string): number {
    const match = /{{\s*(\d+)\s*}}/.exec(token);
    return match ? Number(match[1]) : 0;
  }

  private prepareExtraJson(value: string): string {
    const extraJson = JSON.parse(value || '{}');
    const placeholders = this.placeholderBindings.controls.map((binding) => binding.get('field').value);
    if (placeholders.length) {
      extraJson.placeholders = placeholders;
    }
    return JSON.stringify(extraJson);
  }

  private validExtraJson(control): { invalidExtraJson: true } | null {
    if (!control.value) { return null; }
    try {
      const value = JSON.parse(control.value);
      return value && typeof value === 'object' && !Array.isArray(value)
        ? null
        : { invalidExtraJson: true };
    } catch {
      return { invalidExtraJson: true };
    }
  }
}
